import { narrowedTo, ORGANIZATION, UNKNOWN_POPULATION, type Population } from './figureState.js';

/**
 * WHOSE POPULATION A SCOPED READ COUNTED.
 *
 * This file used to open by saying that `WorkforceBoard.scope` is what it should
 * look like everywhere, that two domains did not have it yet, and that when the
 * server caught up the mirrored predicate should be DELETED and the payload's
 * `scope` read instead.
 *
 * The server has caught up. `GET /supervision/overview` and
 * `GET /credentials/overview` both state `scope: 'organization' | 'caseload'`,
 * resolved by the very predicate that scoped the rows they summarize
 * (`overviewScope` — supervision/service.ts:575, credentials/service.ts:1030).
 * So the two mirrors are gone, and `statedPopulation` below is the whole
 * replacement: read the frame out of the response it is a fact about.
 *
 * WHY DELETING BEAT FIXING, and it is not only tidiness. The mirrors were built
 * on `usePermissions().has`, which reads `/auth/me`'s FLATTENED key list — the
 * grants with `scopeType` and `scopeId` already discarded (F46). A
 * `supervision:manage` devolved to a single program is `true` in that list and
 * `false` to the server's `can()`, so the mirror resolved ORGANIZATION and
 * SUPPRESSED the scope note over a payload the server had explicitly framed as a
 * caseload. The mirror could not be made correct where it stood, because the
 * client is not given the fields the decision is made from. The response is.
 *
 * NOTHING REMAINS. This paragraph used to say that `documentationPopulation`
 * was the last mirror because "`GET /documentation` does not state its scope
 * yet". That was false when it was written. `listDocumentation` returns
 * `{ items, total, scoped }` with `scoped: scope.kind !== 'organization'`
 * (documentation/service.ts:207), the route replies with the whole object
 * (routes.ts:140), and `DocumentationListResponse.scoped` in `@cwos/api-client`
 * is already documented as "True when `total` counts the caller's scope, not the
 * organization".
 *
 * WHY THE CLAIM SURVIVED AS LONG AS IT DID: the frame was on the wire in a
 * DIFFERENT SPELLING — a boolean, rather than the
 * `'organization' | 'caseload'` union the other domains publish — so a search
 * for the union did not find it, and each pass through this file re-read its own
 * header instead of the payload. The documentation screen now reads `scoped`
 * directly, so the mirror had no production caller left and is gone.
 *
 * The rule the whole file is left with: a screen states the frame its response
 * stated, and reconstructs nothing.
 */

/**
 * The values a scoped read frames itself with, across this API.
 *
 * The same words as `adminViews.WorkforceBoard.scope`,
 * `supervision.SupervisionOverview.scope`, `credentials.CredentialOverview.scope`
 * and `programs.ProgramListResult.scope`. Declared once here so a screen reading
 * any of them reaches for the same conversion, and so the next domain that grows
 * a `scope` has an obvious thing to plug into.
 *
 * `'programs'` IS THE SAME FRAME UNDER A DIFFERENT NOUN, not a fourth one.
 * `GET /programs` narrows by `accessibleProgramIds` rather than by caseload, so
 * it names its narrowed arm after what it narrowed by — and the distinction
 * matters to the server and not at all to `statedPopulation`, whose only
 * question is "is this the organization or is it a subset?". Adding the member
 * here is what stopped the programs screen writing its own conversion inline:
 * a second `=== 'organization'` in a screen file is where the two spellings
 * would eventually have disagreed.
 */
export type StatedScope = 'organization' | 'caseload' | 'programs';

/**
 * The server's own answer, turned into the vocabulary a figure is drawn from.
 *
 * `undefined` is the case worth stating: the payload has not arrived, or it
 * failed. That is NOT `caseload` and it is NOT `organization` — both would be a
 * guess, and one of them is the guess that does damage. It is `unknown`, which
 * `figureState` already spells.
 *
 * `who` completes "…of {who}" for the narrowed case and is supplied by the
 * caller, because the SERVER says the read was narrowed and the SCREEN knows
 * what it was narrowed to. Naming it is the moment somebody notices there was a
 * choice, which is the same reason `narrowedTo` demands it.
 */
export function statedPopulation(scope: StatedScope | undefined, who: string): Population {
  if (scope === undefined) return UNKNOWN_POPULATION;
  return scope === 'organization' ? ORGANIZATION : narrowedTo(who);
}

/**
 * The parts of `usePermissions()` a population question needs.
 *
 * `has` — "holds the key at SOME scope" — used to be a member, kept on the
 * strength of a doc comment saying `time/corrections.ts` still read it. It does
 * not; it asks `can`, and so does everything else that takes a `ScopeReader`.
 * The member was a standing invitation to ask the flattened question again,
 * which is the F46 defect this interface exists downstream of, so it is gone.
 * `usePermissions()` still offers `has` for the things that are genuinely
 * gates; this type is not one of them.
 */
export interface ScopeReader {
  /**
   * Holds the key over the ORGANIZATION — the client's mirror of the server's
   * `can()`, and the only question a mirror of an org-wide server predicate may
   * ask. See `features/permissions.ts` for why the two are separate.
   */
  can: (
    key: string,
    scope?: { scopeType: 'org' | 'program' | 'location'; scopeId?: string | null },
  ) => boolean;
  /** The permission set has not arrived. */
  isLoading: boolean;
  /** The permission set could not be read — empty-because-unknown. */
  isError: boolean;
}

/**
 * The sentence for a `<ScopeNote>` beside a narrowed list, or null when the
 * reader reaches everybody.
 *
 * Deliberately says what IS shown and never how much is missing: the client
 * cannot know how many rows the scope removed, and a guess would be the same
 * failure pointed the other way.
 */
export function scopeNoteFor(population: Population): string | null {
  switch (population.kind) {
    case 'organization':
      return null;
    case 'narrowed':
      return population.who;
    case 'unknown':
      return 'what your account reaches — we could not check how far that is';
  }
}
